import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import {
    SearchBill,
    SearchGroupKey,
    SearchPackage,
    SearchPackageConsolidation,
    SearchPrealert,
    SearchResponse,
    SearchResults,
    SearchService,
} from 'app/services/search.service';
import {
    Subject,
    Subscription,
    debounceTime,
    distinctUntilChanged,
    filter,
    takeUntil,
} from 'rxjs';

interface GroupConfig {
    key: SearchGroupKey;
    label: string;
}

interface ClientGroup<T> {
    clientId: number;
    items: T[];
}

@Component({
    selector: 'navbar-search',
    standalone: true,
    templateUrl: './navbar-search.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterLink,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
    ],
})
export class NavbarSearchComponent implements OnInit, OnDestroy {
    @ViewChild('searchOrigin') private _searchOrigin: MatButton;
    @ViewChild('searchPanel') private _searchPanel: TemplateRef<any>;
    @ViewChild('searchInput') private _searchInput: ElementRef<HTMLInputElement>;

    readonly searchControl: UntypedFormControl = new UntypedFormControl('', [
        Validators.minLength(2),
        Validators.maxLength(100),
    ]);

    readonly groups: GroupConfig[] = [
        { key: 'packages', label: 'Paquetes' },
        { key: 'prealerts', label: 'Prealertas' },
        { key: 'clients', label: 'Clientes' },
        { key: 'bills', label: 'Facturas' },
        { key: 'package_consolidations', label: 'Consolidados' },
        { key: 'plan_requests', label: 'Solicitudes de plan' },
        { key: 'admin_notifications', label: 'Notificaciones' },
    ];

    results: SearchResults | null = null;
    loading = false;
    errorMessage: string | null = null;

    groupedPackages: ClientGroup<SearchPackage>[] = [];
    groupedPrealerts: ClientGroup<SearchPrealert>[] = [];
    groupedBills: ClientGroup<SearchBill>[] = [];
    groupedConsolidations: ClientGroup<SearchPackageConsolidation>[] = [];

    private _overlayRef: OverlayRef | null = null;
    private _searchSub: Subscription | null = null;
    private readonly _unsubscribeAll = new Subject<void>();

    private _cdr = inject(ChangeDetectorRef);
    private _overlay = inject(Overlay);
    private _viewContainerRef = inject(ViewContainerRef);
    private _searchService = inject(SearchService);
    private _router = inject(Router);

    ngOnInit(): void {
        this.searchControl.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(300),
                distinctUntilChanged(),
                filter((value) => {
                    const v = (value ?? '').toString().trim();
                    if (v.length < 2) {
                        this.results = null;
                        this.errorMessage = null;
                        this.loading = false;
                        this._cdr.markForCheck();
                        return false;
                    }
                    if (v.length > 100) {
                        this.results = null;
                        this.errorMessage = 'La búsqueda puede tener un máximo de 100 caracteres';
                        this.loading = false;
                        this._cdr.markForCheck();
                        return false;
                    }
                    return true;
                })
            )
            .subscribe((value: string) => {
                this._runSearch(value.trim(), 3);
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this._searchSub?.unsubscribe();
        this._overlayRef?.dispose();
    }

    openPanel(): void {
        if (!this._searchPanel || !this._searchOrigin) {
            return;
        }
        if (!this._overlayRef) {
            this._createOverlay();
        }
        if (this._overlayRef!.hasAttached()) {
            return;
        }
        this._overlayRef!.attach(
            new TemplatePortal(this._searchPanel, this._viewContainerRef)
        );

        setTimeout(() => this._searchInput?.nativeElement?.focus(), 0);
    }

    closePanel(): void {
        if (this._overlayRef?.hasAttached()) {
            this._overlayRef.detach();
        }
        this.searchControl.setValue('');
        this.results = null;
        this.errorMessage = null;
        this.loading = false;
        this._cdr.markForCheck();
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.stopPropagation();
            this.closePanel();
            return;
        }
        if (event.key === 'Enter') {
            const v = (this.searchControl.value ?? '').toString().trim();
            if (v.length >= 2 && v.length <= 100) {
                this.goToFullResults(v);
            }
        }
    }

    goToFullResults(q?: string, fragment?: SearchGroupKey): void {
        const value = (q ?? this.searchControl.value ?? '').toString().trim();
        if (value.length < 2) {
            return;
        }
        this._router.navigate(['/admin/search'], {
            queryParams: { q: value },
            fragment,
        });
        this.closePanel();
    }

    hasAnyResults(): boolean {
        if (!this.results) {
            return false;
        }
        return this.groups.some((g) => (this.results![g.key]?.items?.length ?? 0) > 0);
    }

    trackById = (_: number, item: { id: number }): number => item.id;
    trackByKey = (_: number, group: GroupConfig): string => group.key;
    trackByClientId = <T>(_: number, group: ClientGroup<T>): number => group.clientId;

    private _runSearch(value: string, limit: number): void {
        this._searchSub?.unsubscribe();
        this.loading = true;
        this.errorMessage = null;
        this._cdr.markForCheck();

        this._searchSub = this._searchService.globalSearch(value, limit).subscribe({
            next: (response: SearchResponse) => {
                this.results = response?.results ?? null;
                this._computeGroupedResults();
                this.loading = false;
                this.errorMessage = null;
                this._cdr.markForCheck();
            },
            error: (error) => {
                this.loading = false;
                this.results = null;
                if (error?.status === 422) {
                    this.errorMessage = 'Búsqueda inválida';
                } else if (error?.status === 403) {
                    this.errorMessage = null;
                } else {
                    this.errorMessage = 'Error al realizar la búsqueda';
                }
                this._cdr.markForCheck();
            },
        });
    }

    private _computeGroupedResults(): void {
        if (!this.results) {
            this.groupedPackages = [];
            this.groupedPrealerts = [];
            this.groupedBills = [];
            this.groupedConsolidations = [];
            return;
        }
        this.groupedPackages = this._groupByClient(this.results.packages.items, 'user_id');
        this.groupedPrealerts = this._groupByClient(this.results.prealerts.items, 'client_id');
        this.groupedBills = this._groupByClient(this.results.bills.items, 'client_id');
        this.groupedConsolidations = this._groupByClient(
            this.results.package_consolidations.items,
            'user_id'
        );
    }

    private _groupByClient<T>(items: T[], field: keyof T): ClientGroup<T>[] {
        const map = new Map<number, T[]>();
        for (const item of items) {
            const id = item[field] as unknown as number;
            if (!map.has(id)) {
                map.set(id, []);
            }
            map.get(id)!.push(item);
        }
        return Array.from(map.entries()).map(([clientId, groupItems]) => ({
            clientId,
            items: groupItems,
        }));
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            panelClass: 'navbar-search-overlay-panel',
            width: 'min(720px, calc(100vw - 32px))',
            maxHeight: 'calc(100vh - 96px)',
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._searchOrigin._elementRef.nativeElement)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                        offsetY: 8,
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                        offsetY: -8,
                    },
                ]),
        });

        this._overlayRef.backdropClick().subscribe(() => this.closePanel());
        this._overlayRef.keydownEvents().subscribe((event) => {
            if (event.key === 'Escape') {
                this.closePanel();
            }
        });
    }
}
